import { useT } from "@transifex/react";
import moment from "moment";
import type { Moment } from "moment";
import { ShareModes } from "../../../enums/shareFlow";
import { AreaButton, ButtonVariant } from "../../teacherArea/AreaButton";
import CollapsibleFormSection from "../../../basic/collapsibleFormSection/CollapsibleFormSection";
import DateTimeInput from "../../../basic/dateTimeInput/DateTimeInput";
import DurationInput from "../../../basic/durationInput/DurationInput";
import FormFieldLabel from "../../../basic/formFieldLabel/FormFieldLabel";

interface ShareDetailsStepProps {
    selectedMode: ShareModes | null;
    message: string;
    onMessageChange: (message: string) => void;
    showDeadline: boolean;
    onToggleDeadline: () => void;
    deadlineValue: Moment | null;
    onDeadlineChange: (value: Moment | null) => void;
    visibilityDate?: Moment | null;
    showDuration: boolean;
    onToggleDuration: () => void;
    durationHours: string;
    durationMinutes: string;
    onDurationHoursSelect: (value: number) => void;
    onDurationMinutesSelect: (value: number) => void;
    disabled: boolean;
}

const ShareDetailsStep = (props: ShareDetailsStepProps) => {
    const t = useT();

    const visibilityDate =
        props.visibilityDate && props.visibilityDate.isValid() && props.visibilityDate.isAfter(moment())
            ? props.visibilityDate
            : undefined;
    const deadlineMinDate = visibilityDate ?? moment();

    const t_messageLabel = t("Your message for your students", { _tags: "ShareFlow, ShareDetailsStep" });
    const t_messagePlaceholder = t("Enter message...", { _tags: "ShareFlow, ShareDetailsStep" });
    const t_deadline = t("Deadline", { _tags: "ShareFlow, ShareDetailsStep" });
    const t_duration = t("Processing time", { _tags: "ShareFlow, ShareDetailsStep" });
    const t_selectHours = t("Select hour(s)", { _tags: "ShareFlow, ShareDetailsStep" });
    const t_selectMinutes = t("Select minutes", { _tags: "ShareFlow, ShareDetailsStep" });
    const t_removeDeadline = t("Remove deadline", { _tags: "ShareFlow, ShareDetailsStep, ScreenReaderOnly" });
    const t_removeDuration = t("Remove processing time", { _tags: "ShareFlow, ShareDetailsStep, ScreenReaderOnly" });
    const t_deadlineInfoText = visibilityDate
        ? t("The test will be visible on {date}, {time}. The deadline cannot be before that.", {
              _tags: "ShareFlow, ShareDetailsStep",
              date: visibilityDate.format("DD.MM.YYYY"),
              time: visibilityDate.format("HH:mm"),
          })
        : undefined;

    const renderAddButton = (label: string, onClick: () => void) => {
        return (
            <AreaButton
                variant={ButtonVariant.SECONDARY}
                role="button"
                onClick={onClick}
                iconName="add"
                disabled={props.disabled}
            >
                {label}
            </AreaButton>
        );
    };

    const renderTimingExtras = () => {
        if (props.selectedMode !== ShareModes.TEST) return null;

        // pointer-events-none also blocks the trash remove icon and the date/duration inputs, which don't expose a disabled prop.
        const disabledClasses = props.disabled ? "pointer-events-none opacity-50" : "";

        return (
            <>
                <div className="flex gap-s flex-wrap">
                    {!props.showDeadline && renderAddButton(t_deadline, props.onToggleDeadline)}
                    {!props.showDuration && renderAddButton(t_duration, props.onToggleDuration)}
                </div>

                {props.showDeadline && (
                    <div className={disabledClasses}>
                        <CollapsibleFormSection
                            label={t_deadline}
                            onRemove={props.onToggleDeadline}
                            iconAriaLabel={t_removeDeadline}
                        >
                            <DateTimeInput
                                value={props.deadlineValue}
                                onChange={props.onDeadlineChange}
                                minDate={deadlineMinDate}
                                minTime={visibilityDate}
                                infoText={t_deadlineInfoText}
                            />
                        </CollapsibleFormSection>
                    </div>
                )}

                {props.showDuration && (
                    <div className={disabledClasses}>
                        <CollapsibleFormSection
                            label={t_duration}
                            onRemove={props.onToggleDuration}
                            iconAriaLabel={t_removeDuration}
                        >
                            <DurationInput
                                hours={props.durationHours}
                                minutes={props.durationMinutes}
                                hoursPlaceholder={t_selectHours}
                                minutesPlaceholder={t_selectMinutes}
                                onHoursSelect={props.onDurationHoursSelect}
                                onMinutesSelect={props.onDurationMinutesSelect}
                            />
                        </CollapsibleFormSection>
                    </div>
                )}
            </>
        );
    };

    return (
        <div className="flex flex-col gap-s w-full">
            <FormFieldLabel label={t_messageLabel}>
                <textarea
                    data-testid="share-details-message"
                    value={props.message}
                    onChange={(e) => props.onMessageChange(e.target.value)}
                    placeholder={t_messagePlaceholder}
                    disabled={props.disabled}
                    className="w-full min-h-[182px] border border-p6-grey-dark rounded-small-container p-s text-m text-p6-grey-dark resize-none focus:outline-none focus:border-p6-orange disabled:opacity-50 disabled:cursor-not-allowed"
                />
            </FormFieldLabel>

            {renderTimingExtras()}
        </div>
    );
};

export default ShareDetailsStep;
